/**
 * VH-126: ONE capture screen, not three.
 *
 * Per the UI decision (Proposal B), the user never picks a mode. They point
 * the camera at a place and shoot 1…n photos; the "modes" in the spec are
 * simply where they stop. Shooting one photo and acting on it is the busy-gym
 * case; shooting several and reviewing them is the semi-busy case.
 *
 * The consequence for this component: the first photo takes the fast path
 * (one synchronous call, answer in seconds). Reaching for a second photo
 * silently promotes the session to a multi-photo scan. There is no mode
 * switch to find because there is no mode.
 *
 * Video capture is not here yet — it needs resumable chunked upload, which is
 * a separate piece of work.
 */

import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CameraIcon, PlusIcon } from '@heroicons/react/24/outline'
import { trainingLocationsAPI, validatePhoto } from '../../services/trainingLocations'

export default function CaptureFlow({ locationId, onScanReady, onQuickResult }) {
  const { t } = useTranslation()
  const inputRef = useRef(null)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  // Set once a second photo promotes this into a multi-photo scan.
  const [scanId, setScanId] = useState(null)
  const [shotCount, setShotCount] = useState(0)

  const handleFile = async (event) => {
    const file = event.target.files?.[0]
    // Reset immediately so shooting the same machine twice still fires change.
    event.target.value = ''
    if (!file) return

    const invalid = validatePhoto(file)
    if (invalid) {
      setError(t(invalid.key, invalid.values))
      return
    }

    setError(null)
    setBusy(true)
    try {
      if (!scanId && shotCount === 0) {
        // First photo: the fast path. One call, one machine, no polling.
        const result = await trainingLocationsAPI.quickScan(locationId, file)
        setShotCount(1)
        setScanId(result.data.scan_id)
        onQuickResult?.(result.data)
      } else {
        // Second and later photos join the scan the first one opened.
        const result = await trainingLocationsAPI.addPhoto(scanId, file)
        setShotCount((n) => n + 1)
        onScanReady?.(result.data)
      }
    } catch (err) {
      // A failed photo is per-photo: whatever landed before is still there.
      setError(
        err?.response?.data?.detail || t('trainingLocations.errors.scanFailed')
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div data-testid="capture-flow">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFile}
        className="hidden"
        data-testid="capture-input"
      />

      <button
        type="button"
        className="btn-primary w-full flex items-center justify-center gap-2 py-4 text-lg"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid="capture-button"
      >
        {shotCount === 0 ? (
          <>
            <CameraIcon className="h-6 w-6" />
            {busy ? t('trainingLocations.capture.working') : t('trainingLocations.capture.start')}
          </>
        ) : (
          <>
            <PlusIcon className="h-6 w-6" />
            {busy
              ? t('trainingLocations.capture.working')
              : t('trainingLocations.capture.another', { count: shotCount })}
          </>
        )}
      </button>

      {error && (
        <p className="mt-3 text-sm text-red-600" role="alert" data-testid="capture-error">
          {error}
        </p>
      )}
    </div>
  )
}
